import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const cardVariants = cva(
  "bg-card text-card-foreground flex flex-col rounded-lg border border-border shadow-xs",
  {
    variants: {
      variant: {
        default: "gap-6 py-6",
        compact: "gap-4 py-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const CardContext = React.createContext<{
  variant: "default" | "compact";
}>({
  variant: "default",
});

function Card({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  const cardVariant = variant || "default";

  return (
    <CardContext.Provider value={{ variant: cardVariant }}>
      <div
        data-slot="card"
        className={cn(cardVariants({ variant: cardVariant }), className)}
        {...props}
      />
    </CardContext.Provider>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  const { variant } = React.useContext(CardContext);

  const paddingClasses =
    variant === "compact" ? "px-4 [.border-b]:pb-4" : "px-6 [.border-b]:pb-6";

  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header gap-x-1.5 gap-y-0 has-data-[slot=card-action]:grid has-data-[slot=card-action]:auto-rows-min has-data-[slot=card-action]:grid-rows-[auto_auto] has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-action]:items-start",
        paddingClasses,
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-medium", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        "text-muted-foreground font-regular text-sm mt-1",
        className
      )}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  const { variant } = React.useContext(CardContext);

  const paddingClasses = variant === "compact" ? "px-4" : "px-6";

  return (
    <div
      data-slot="card-content"
      className={cn(paddingClasses, className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  const { variant } = React.useContext(CardContext);

  const paddingClasses =
    variant === "compact" ? "px-4 [.border-t]:pt-4" : "px-6 [.border-t]:pt-6";

  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center", paddingClasses, className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
